<script setup lang="ts">
import { computed, ref } from 'vue'
import ServiceModelHttp from './ServiceModelHttp.vue'
import type { HttpServiceModelBase, HttpServiceModelPaging, NextPageRequest } from '@/types'
import { applyPreset, pagingPresets, pagingRoots } from './paging-options'

const props = defineProps<{
  modelValue: HttpServiceModelPaging
}>()

const emit = defineEmits<{
  'update:modelValue': [value: HttpServiceModelPaging]
}>()

// The preset an author last picked, held here and nowhere else. Nothing persists it: it wrote into the
// two expression fields and its job is done, so a saved service records the expressions alone.
const pickedPreset = ref('')

function update(updates: Partial<HttpServiceModelPaging>) {
  emit('update:modelValue', { ...props.modelValue, ...updates })
}

// The HTTP editor is typed to the shared base, so it hands back only the fields it owns. $type is
// restated because a spread of the base would widen it back to a plain string.
function updateHttp(value: HttpServiceModelBase) {
  emit('update:modelValue', { ...props.modelValue, ...value, $type: 'httpPaging' })
}

const nextPage = computed<NextPageRequest>(() => props.modelValue.nextPage ?? { query: {} })

function updateNextPage(updates: Partial<NextPageRequest>) {
  update({ nextPage: { ...nextPage.value, ...updates } })
}

// The query map is edited as an ordered list of rows so a half-typed name does not collide with another
// row's, and is folded back to a map on every keystroke. A blank name drops out of the map and comes
// back the moment it is typed into.
const queryRows = ref<{ name: string; value: string }[]>(
  Object.entries(nextPage.value.query ?? {}).map(([name, value]) => ({ name, value })),
)

function emitQueryRows() {
  const query: Record<string, string> = {}
  for (const row of queryRows.value) if (row.name) query[row.name] = row.value
  updateNextPage({ query })
}

function addQueryRow() {
  queryRows.value.push({ name: '', value: '' })
}

function removeQueryRow(index: number) {
  queryRows.value.splice(index, 1)
  emitQueryRows()
}

function pickPreset(id: string) {
  pickedPreset.value = id
  const preset = pagingPresets.find((p) => p.id === id)
  if (!preset) return
  const applied = applyPreset(props.modelValue, preset)
  queryRows.value = Object.entries(applied.nextPage?.query ?? {}).map(([name, value]) => ({ name, value }))
  emit('update:modelValue', applied)
}

const presetHint = computed(() => pagingPresets.find((p) => p.id === pickedPreset.value)?.hint ?? '')
</script>

<template>
  <div>
    <ServiceModelHttp :model-value="modelValue" @update:model-value="updateHttp" />

    <hr class="my-4" />

    <datalist id="paging-scope-suggestions">
      <option v-for="root in pagingRoots" :key="root.path" :value="root.path">{{ root.hint }}</option>
    </datalist>

    <h6 class="mb-2">Paging</h6>
    <p class="form-text mt-0 mb-3">
      The service keeps asking for the next page while the has-next expression is true, and publishes
      every page as one set. Each page's request is the request above with the changes below written
      over it.
    </p>

    <div class="mb-3">
      <label class="form-label">Start from</label>
      <select
        class="form-select form-select-sm"
        :value="pickedPreset"
        @change="pickPreset(($event.target as HTMLSelectElement).value)"
      >
        <option value="" disabled>Pick the shape this vendor pages by…</option>
        <option v-for="preset in pagingPresets" :key="preset.id" :value="preset.id">
          {{ preset.label }}
        </option>
      </select>
      <div v-if="presetHint" class="form-text">{{ presetHint }}</div>
      <div class="form-text">
        A preset fills the fields below and is then forgotten — edit them freely afterwards.
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label">Another page follows while</label>
      <input
        type="text"
        class="form-control form-control-sm font-monospace"
        list="paging-scope-suggestions"
        :value="modelValue.hasNextPageExpression"
        @input="update({ hasNextPageExpression: ($event.target as HTMLInputElement).value })"
        placeholder="response.body.hasMore"
      />
      <div class="form-text">
        An expression over <code>response.*</code> and <code>paging.page</code>, written without
        <code v-pre>{{ }}</code>. Required.
      </div>
    </div>

    <div class="card bg-light-subtle mb-3">
      <div class="card-body py-3">
        <h6 class="card-title mb-1">The next page's request</h6>
        <p class="form-text mt-0 mb-3">
          Only what moves between pages. Everything left blank carries forward from the request above.
        </p>

        <div class="mb-3">
          <label class="form-label">URL</label>
          <input
            type="text"
            class="form-control form-control-sm font-monospace"
            list="paging-scope-suggestions"
            :value="nextPage.url ?? ''"
            @input="updateNextPage({ url: ($event.target as HTMLInputElement).value || undefined })"
            placeholder="response.body['@odata.nextLink']"
          />
          <div class="form-text">Replaces the whole address, where the vendor hands one back.</div>
        </div>

        <div class="mb-3">
          <label class="form-label">Query parameters</label>
          <div
            v-for="(row, index) in queryRows"
            :key="index"
            class="d-flex align-items-center gap-2 mb-2"
          >
            <input
              type="text"
              class="form-control form-control-sm font-monospace"
              style="max-width: 12rem"
              v-model="row.name"
              @input="emitQueryRows"
              placeholder="cursor"
            />
            <input
              type="text"
              class="form-control form-control-sm font-monospace"
              list="paging-scope-suggestions"
              v-model="row.value"
              @input="emitQueryRows"
              placeholder="response.body.nextCursor"
            />
            <button type="button" class="btn btn-outline-danger btn-sm" @click="removeQueryRow(index)">
              <i class="bi bi-trash"></i>
            </button>
          </div>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="addQueryRow">
            <i class="bi bi-plus-lg me-1"></i>Add parameter
          </button>
          <div class="form-text">
            Set on the next request, over whatever the address already carries. Parameters not named
            here are left alone.
          </div>
        </div>

        <div>
          <label class="form-label">Body</label>
          <textarea
            class="form-control form-control-sm font-monospace"
            rows="2"
            :value="nextPage.body ?? ''"
            @input="updateNextPage({ body: ($event.target as HTMLTextAreaElement).value || undefined })"
            placeholder="jsonEncode({ offset: response.body.offset, count: 500 })"
          ></textarea>
          <div class="form-text">
            One expression for the entire body, where the vendor pages inside it.
          </div>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label">Page cap</label>
      <input
        type="number"
        min="1"
        class="form-control form-control-sm"
        style="max-width: 10rem"
        :value="modelValue.maxPages"
        @input="update({ maxPages: Number(($event.target as HTMLInputElement).value) })"
      />
      <div class="form-text">
        Reaching the cap fails the transaction rather than publishing a short pull, so a vendor that
        always claims another page cannot run forever.
      </div>
    </div>
  </div>
</template>
